/**
 * Variant-specific media gallery filtering for wall art products.
 * Filters product images by size and/or frame color based on
 * [size:value] and [frame:value] tags in image alt text,
 * parsed into data-media-size and data-media-frame attributes by Liquid.
 *
 * Canvas products filter on size only.
 * Framed products filter on size AND frame color (strict match on both).
 * Untagged images (empty data attributes) are always visible.
 */
document.addEventListener('DOMContentLoaded', () => {
  const HIDDEN_CLASS = 'media-item--color-hidden';

  // Read filter config: { "size": <optionPosition>, "frame": <optionPosition> }
  const configEl = document.querySelector('[id^="MahjWallartFilterConfig"]');
  if (!configEl) return;

  let filterConfig;
  try {
    filterConfig = JSON.parse(configEl.textContent.trim());
  } catch (e) {
    return;
  }

  // Build a map of data-attribute name → variant option key (option1, option2, option3)
  const filters = {};
  if (filterConfig.size) filters.size = `option${filterConfig.size}`;
  if (filterConfig.frame) filters.frame = `option${filterConfig.frame}`;

  if (Object.keys(filters).length === 0) return;

  function getFilterValues(variant) {
    if (!variant) return null;
    const values = {};
    for (const [attr, optionKey] of Object.entries(filters)) {
      const val = variant[optionKey];
      values[attr] = val ? val.toLowerCase() : null;
    }
    return values;
  }

  function isVisible(li, filterValues) {
    for (const [attr, selectedValue] of Object.entries(filterValues)) {
      if (!selectedValue) continue;
      const tag = (li.dataset[`media${attr.charAt(0).toUpperCase() + attr.slice(1)}`] || '').toLowerCase();
      // Untagged dimension = always visible for that dimension
      if (tag === '') continue;
      // Tagged dimension must match exactly
      if (tag !== selectedValue) return false;
    }
    return true;
  }

  function filterGallery(filterValues) {
    const gallery = document.querySelector('media-gallery');
    if (!gallery) return;

    const viewer = gallery.querySelector('[id^="GalleryViewer"] ul');
    if (!viewer) return;

    const activeMedia = viewer.querySelector('li.is-active');
    let activeIsHidden = false;
    let firstVisible = null;

    // Filter main gallery items
    viewer.querySelectorAll('li[data-media-id]').forEach((li) => {
      const visible = isVisible(li, filterValues);
      if (visible) {
        li.classList.remove(HIDDEN_CLASS);
        li.removeAttribute('aria-hidden');
        if (!firstVisible) firstVisible = li;
      } else {
        li.classList.add(HIDDEN_CLASS);
        li.setAttribute('aria-hidden', 'true');
        if (li === activeMedia) activeIsHidden = true;
      }
    });

    // Sync thumbnails
    const thumbList = gallery.querySelector('[id^="Slider-Thumbnails"]');
    if (thumbList) {
      thumbList.querySelectorAll('li[data-target]').forEach((li) => {
        const visible = isVisible(li, filterValues);
        if (visible) {
          li.classList.remove(HIDDEN_CLASS);
          li.removeAttribute('aria-hidden');
          li.querySelectorAll('button').forEach((b) => b.removeAttribute('tabindex'));
        } else {
          li.classList.add(HIDDEN_CLASS);
          li.setAttribute('aria-hidden', 'true');
          li.querySelectorAll('button').forEach((b) => b.setAttribute('tabindex', '-1'));
        }
      });
    }

    // Switch to first visible image if current active is hidden
    if (activeIsHidden && firstVisible) {
      const mediaId = firstVisible.dataset.mediaId;
      gallery.setActiveMedia?.(mediaId, false);
    }

    // Update slider counter
    const counter = gallery.querySelector('.slider-counter--total');
    if (counter) {
      const visibleCount = viewer.querySelectorAll(`li[data-media-id]:not(.${HIDDEN_CLASS})`).length;
      counter.textContent = visibleCount;
    }
  }

  // Listen for variant changes
  subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
    requestAnimationFrame(() => {
      const values = getFilterValues(event.data.variant);
      if (values) filterGallery(values);
    });
  });

  // Initial page load: filter based on pre-selected variant
  const sectionId = configEl.id.replace('MahjWallartFilterConfig-', '');
  const sectionEl = document.querySelector(`product-info[data-section="${sectionId}"]`);
  if (sectionEl) {
    const variantJson = sectionEl.querySelector('variant-selects [data-selected-variant]');
    if (variantJson) {
      try {
        const variant = JSON.parse(variantJson.innerHTML);
        const values = getFilterValues(variant);
        if (values) filterGallery(values);
      } catch (e) {
        /* no-op */
      }
    }
  }
});
